import { SpinResponse } from '@cgs/data-access';
import { Logger } from '@cgs/shared';
import {
  Completer,
  Container,
  FactoryFunc,
  IDisposable,
  InstanceLifetime,
  IStreamSubscription,
  SceneObject,
} from '@cgs/wge';
import { FreeSpinsSoundModelComponent } from '../game/components/free_spins_sound_model_component';
import { GameStateMachineProvider } from '../game/components/game-state-machine.provider';
import { IconModelComponent } from '../game/components/icon_model_component';
import { IconsSceneObjectComponent } from '../game/components/icons_scene_object_component';
import { IconsSoundModelComponent } from '../game/components/icons_sound_model_component';
import { RegularSpinsSoundModelComponent } from '../game/components/regular_spins_sound_model_component';
import { ResourcesComponent } from '../game/components/resources_component';
import { SlotPrimaryAnimationProvider } from '../game/components/slot_primary_animation_provider';
import { SlotSpinParams } from '../reels_engine/game_components/i_spin_params';
import {
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  IGameStateMachineProvider,
} from '../reels_engine/game_components_providers/i_game_state_machine_provider';
import { ISlotGameEngineProvider } from '../reels_engine/game_components_providers/i_slot_game_engine_provider';
import { ISlotGame } from '../reels_engine/i_slot_game';
import { ISlotGameEngine } from '../reels_engine/i_slot_game_engine';
import { IGameParams } from '../reels_engine/interfaces/i_game_params';
import { GameStateMachine } from '../reels_engine/state_machine/game-state-machine';
import {
  T_BaseSlotGame,
  T_FreeSpinsSoundModelComponent,
  T_IconModelComponent,
  T_IconsSceneObjectComponent,
  T_IconsSoundModelComponent,
  T_IGameParams,
  T_ISlotGame,
  T_ISlotGameEngineProvider,
  T_ISlotPrimaryAnimationProvider,
  T_ISpinParams,
  T_RegularSpinsSoundModelComponent,
  RESOURCES_COMPONENT_TOKEN,
} from '../type_definitions';
import {
  TIME_SCALE_COMPONENT_TOKEN,
  TimeScaleComponent,
} from '../game/components/time-scale.component';

export abstract class AbstractSlotGame extends SceneObject implements IDisposable {
  private readonly _gameParams: IGameParams;

  private _slotLoadingCompleter: Completer<ISlotGame> = new Completer();
  private _slotStateMachineLoaded: Completer<boolean> = new Completer();
  private _slotInitStateSub: IStreamSubscription | null = null;
  private _engine: ISlotGameEngine;
  private _gameStateMachine: GameStateMachine<SpinResponse>;
  private _timeScaleComponent: TimeScaleComponent;
  private _isInitialized: boolean = false;

  constructor(
    gameParams: IGameParams,
    public container: Container
  ) {
    super();
    this._gameParams = gameParams;
  }

  get gameParams(): IGameParams {
    return this._gameParams;
  }

  get slotLoadingCompleter(): Completer<ISlotGame> {
    return this._slotLoadingCompleter;
  }

  get slotStateMachineLoaded(): Completer<boolean> {
    return this._slotStateMachineLoaded;
  }

  get engine(): ISlotGameEngine {
    return this._engine;
  }

  get gameStateMachine(): GameStateMachine<SpinResponse> {
    return this._gameStateMachine;
  }

  get isInitialized(): boolean {
    return this._isInitialized;
  }

  get timeScaleComponent(): TimeScaleComponent {
    if (!this._timeScaleComponent) {
      this._timeScaleComponent = this.container.resolve<TimeScaleComponent>(
        TIME_SCALE_COMPONENT_TOKEN
      );
    }
    return this._timeScaleComponent;
  }

  start(): void {}

  initialize(): void {
    if (!this._isInitialized) {
      super.initialize();
      this.initializeInternal();
      this._isInitialized = true;
    }
  }

  async initializeInternal(): Promise<void> {
    try {
      this.initSlotComponents();
      await this.loadResources();
      await this.initSlot();
      // TODO: AbstractSlotGame и ISlotGame не совместимы
      this._slotLoadingCompleter.complete(this as unknown as ISlotGame);
    } catch (error) {
      this._slotLoadingCompleter.completeError(error);
    }
  }

  initSlotComponents(): void {
    this.initComponentContainer();
  }

  initComponentContainer(): void {
    this.registerAsSingleInstance(() => {
      Logger.Debug('load ' + 'ISlotGame');
      return this;
    }, T_ISlotGame);
    this.registerAsSingleInstance(() => {
      Logger.Debug('load ' + 'IGameParams');
      return this.gameParams;
    }, T_IGameParams);
    this.registerAsSingleInstance(() => {
      Logger.Debug('load ' + 'BaseSlotGame');
      return this;
    }, T_BaseSlotGame);

    this.registerAsSingleInstance((c) => {
      return new ResourcesComponent(c, this);
    }, RESOURCES_COMPONENT_TOKEN);
    this.registerAsSingleInstance((c) => {
      Logger.Debug('load ' + 'GameStateMachineProvider');
      return new GameStateMachineProvider(c);
    }, GAME_STATE_MACHINE_PROVIDER_TOKEN);
    this.registerAsSingleInstance((c) => {
      Logger.Debug('load ' + 'IconModelComponent');
      return new IconModelComponent(c);
    }, T_IconModelComponent);
    this.registerAsSingleInstance((c) => {
      return new IconsSceneObjectComponent(c);
    }, T_IconsSceneObjectComponent);
    this.registerAsSingleInstance((c) => {
      return new IconsSoundModelComponent(c);
    }, T_IconsSoundModelComponent);
    this.registerAsSingleInstance((c) => {
      return new RegularSpinsSoundModelComponent(c);
    }, T_RegularSpinsSoundModelComponent);
    this.registerAsSingleInstance((c) => {
      Logger.Debug('load ' + 'FreeSpinsSoundModelComponent');
      return new FreeSpinsSoundModelComponent(c);
    }, T_FreeSpinsSoundModelComponent);
    this.registerAsSingleInstance((c) => {
      Logger.Debug('load ' + 'SlotAnimationComponent');
      return new SlotPrimaryAnimationProvider(c);
    }, T_ISlotPrimaryAnimationProvider);
    this.registerAsSingleInstance(() => {
      Logger.Debug('load ' + 'ISpinParams');
      return new SlotSpinParams();
    }, T_ISpinParams);
    this.registerAsSingleInstance(() => {
      return new TimeScaleComponent(this.container);
    }, TIME_SCALE_COMPONENT_TOKEN);
  }

  loadResources(): Promise<void> {
    return new Promise<void>(() => {});
  }

  // BaseSlotGame overrides this with an async implementation, so callers must await it
  initSlot(): void | Promise<void> {
    const reelsEngineProvider =
      this.container.resolve<ISlotGameEngineProvider>(T_ISlotGameEngineProvider);
    const gameStateMachineProvider = this.container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    );
    this._gameStateMachine =
      gameStateMachineProvider?.gameStateMachine as GameStateMachine<SpinResponse>;
    this._slotInitStateSub = this._gameStateMachine.init.leaved.listen(() => {
      this._slotStateMachineLoaded.complete(true);
      this._slotInitStateSub?.cancel();
    });
    this._engine = reelsEngineProvider?.gameEngine as ISlotGameEngine;
  }

  protected registerAsSingleInstance<T>(factory: FactoryFunc<T>, type: symbol): void {
    this.container.register(type, factory, { lifetime: InstanceLifetime.Singleton });
  }

  // Clamp dt to prevent large spikes (e.g. iOS Safari pauses rAF during orientation change
  // without firing visibilitychange, producing dt up to 1 s on resume which corrupts reel positions)
  private static readonly _MAX_SAFE_DT = 1 / 30;

  update(dt: number): void {
    if (!this.active) {
      return;
    }

    const safeDt = Math.min(dt, AbstractSlotGame._MAX_SAFE_DT);

    if (this.timeScaleComponent && !this.timeScaleComponent.isPaused) {
      const scaledDt = safeDt * this.timeScale;
      if (this._gameStateMachine) {
        this._gameStateMachine.update(scaledDt);
      }
      this.updateEngine(scaledDt);
    }

    super.update(safeDt);
  }

  updateEngine(dt: number): void {
    if (this._engine) {
      this._engine.update(dt);
    }
  }

  dispose(): void {
    this.container
      .resolve<IGameStateMachineProvider>(GAME_STATE_MACHINE_PROVIDER_TOKEN)
      .deinitialize();
    const res: ResourcesComponent =
      this.container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);
    res.unload();
    this.container.resolve<IconModelComponent>(T_IconModelComponent).dispose();
  }
}
